<template>
  <section class="section section-drama">
    <div class="tech-bg">
      <div class="grid-overlay"></div>
      <div class="floating-particles">
        <div class="particle" v-for="n in 30" :key="n" :style="particleStyle(n)"></div>
      </div>
      <div class="glow-orb glow-orb-1"></div>
      <div class="glow-orb glow-orb-2"></div>
      <div class="glow-orb glow-orb-3"></div>
      <div class="hex-grid"></div>
    </div>
    <div class="container">
      <div class="page-head">
        <h2>AI 视频生成</h2>
        <p>将您的创意转化为精彩的视频内容</p>
      </div>
      <div class="card">
        <!-- 标签页 -->
        <div class="tab-container">
          <button 
            class="tab-btn" 
            :class="{ active: activeTab === 'text' }"
            @click="activeTab = 'text'"
          >
            📝 文生视频
          </button>
          <button 
            class="tab-btn" 
            :class="{ active: activeTab === 'image' }"
            @click="activeTab = 'image'"
          >
            🖼️ 图生视频
          </button>
          <button 
            class="tab-btn" 
            :class="{ active: activeTab === 'avatar' }"
            @click="activeTab = 'avatar'"
          >
            👤 数字人对口型
          </button>
        </div>
        
        <!-- 文生视频 -->
        <div v-if="activeTab === 'text'" class="tab-content">
          <div class="form-item">
            <label>提示词</label>
            <textarea
              v-model="form.prompt"
              :rows="4"
              placeholder="请输入详细的视频描述"
              class="textarea"
            ></textarea>
          </div>
          <div class="form-row">
            <div class="form-item half">
              <label>模型选择</label>
              <div class="model-select-container">
                <div class="selected-model" @click="toggleModelDropdown('video')">
                  <img v-if="form.model && models[form.model]?.image_url" :src="models[form.model]?.image_url" :alt="models[form.model]?.name" class="model-icon">
                  <span v-if="form.model && models[form.model]">{{ models[form.model]?.name }}</span>
                  <span v-else>选择模型</span>
                  <span class="dropdown-arrow">▼</span>
                </div>
                <div v-if="modelDropdown === 'video'" class="model-dropdown">
                  <div class="model-group">
                    <div class="model-group-title">🌟 免费模型</div>
                    <div v-for="id in freeModels" :key="id" class="model-item" @click="selectModel('video', id)">
                      <img :src="models[id]?.image_url" :alt="models[id]?.name" class="model-icon">
                      <span class="model-name">{{ models[id]?.name }}</span>
                      <div class="model-tags">
                        <span v-if="models[id]?.is_new" class="model-tag new">NEW</span>
                        <span v-if="models[id]?.is_hot" class="model-tag hot">HOT</span>
                        <span v-if="models[id]?.discount > 0" class="model-tag discount">{{ models[id]?.discount }}% OFF</span>
                      </div>
                    </div>
                  </div>
                  <div class="model-group">
                    <div class="model-group-title">💎 付费模型</div>
                    <div v-for="id in paidModels" :key="id" class="model-item" @click="selectModel('video', id)">
                      <img :src="models[id]?.image_url" :alt="models[id]?.name" class="model-icon">
                      <span class="model-name">{{ models[id]?.name }}</span>
                      <div class="model-tags">
                        <span v-if="models[id]?.is_new" class="model-tag new">NEW</span>
                        <span v-if="models[id]?.is_hot" class="model-tag hot">HOT</span>
                        <span v-if="models[id]?.discount > 0" class="model-tag discount">{{ models[id]?.discount }}% OFF</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="model-hint" v-if="form.model && models[form.model]">
                <span :class="['model-type', models[form.model].type]">
                  {{ models[form.model].type === 'free' ? '免费' : '付费' }}
                </span>
                <span class="model-desc">{{ models[form.model].description }}</span>
              </div>
            </div>
            <div class="form-item half">
              <label>视频时长</label>
              <select v-model="form.duration" class="select">
                <option v-for="dur in videoDurations" :key="dur.duration" :value="dur.duration">{{ dur.name }}</option>
              </select>
            </div>
          </div>
          <div class="form-item">
            <label>视频尺寸</label>
            <select v-model="form.size" class="select">
              <option v-for="size in videoSizes" :key="size.name" :value="size.aspect_ratio">{{ size.name }}</option>
            </select>
          </div>
        </div>
        
        <!-- 图生视频 -->
        <div v-if="activeTab === 'image'" class="tab-content">
          <div class="form-item">
            <label>上传参考图片</label>
            <div class="upload-area" @click="triggerImageUpload">
              <input 
                type="file" 
                ref="imageInput" 
                style="display: none" 
                accept="image/*" 
                @change="handleImageUpload"
              >
              <div v-if="form.referenceImage" class="uploaded-image">
                <img :src="form.referenceImage" alt="参考图片" class="preview-image">
                <button class="remove-btn" @click.stop="removeImage">×</button>
              </div>
              <div v-else class="upload-placeholder">
                <div class="upload-icon">📁</div>
                <p>点击上传图片</p>
                <p class="hint">支持 JPG、PNG 格式</p>
              </div>
            </div>
          </div>
          <div class="form-item">
            <label>提示词</label>
            <textarea
              v-model="form.prompt"
              :rows="3"
              placeholder="请输入详细的视频描述"
              class="textarea"
            ></textarea>
          </div>
          <div class="form-row">
            <div class="form-item half">
              <label>模型选择</label>
              <div class="model-select-container">
                <div class="selected-model" @click="toggleModelDropdown('video')">
                  <img v-if="form.model && models[form.model]?.image_url" :src="models[form.model]?.image_url" :alt="models[form.model]?.name" class="model-icon">
                  <span v-if="form.model && models[form.model]">{{ models[form.model]?.name }}</span>
                  <span v-else>选择模型</span>
                  <span class="dropdown-arrow">▼</span>
                </div>
                <div v-if="modelDropdown === 'video'" class="model-dropdown">
                  <div class="model-group">
                    <div class="model-group-title">🌟 免费模型</div>
                    <div v-for="id in freeModels" :key="id" class="model-item" @click="selectModel('video', id)">
                      <img :src="models[id]?.image_url" :alt="models[id]?.name" class="model-icon">
                      <span class="model-name">{{ models[id]?.name }}</span>
                      <div class="model-tags">
                        <span v-if="models[id]?.is_new" class="model-tag new">NEW</span>
                        <span v-if="models[id]?.is_hot" class="model-tag hot">HOT</span>
                        <span v-if="models[id]?.discount > 0" class="model-tag discount">{{ models[id]?.discount }}% OFF</span>
                      </div>
                    </div>
                  </div>
                  <div class="model-group">
                    <div class="model-group-title">💎 付费模型</div>
                    <div v-for="id in paidModels" :key="id" class="model-item" @click="selectModel('video', id)">
                      <img :src="models[id]?.image_url" :alt="models[id]?.name" class="model-icon">
                      <span class="model-name">{{ models[id]?.name }}</span>
                      <div class="model-tags">
                        <span v-if="models[id]?.is_new" class="model-tag new">NEW</span>
                        <span v-if="models[id]?.is_hot" class="model-tag hot">HOT</span>
                        <span v-if="models[id]?.discount > 0" class="model-tag discount">{{ models[id]?.discount }}% OFF</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="model-hint" v-if="form.model && models[form.model]">
                <span :class="['model-type', models[form.model].type]">
                  {{ models[form.model].type === 'free' ? '免费' : '付费' }}
                </span>
                <span class="model-desc">{{ models[form.model].description }}</span>
              </div>
            </div>
            <div class="form-item half">
              <label>视频时长</label>
              <select v-model="form.duration" class="select">
                <option v-for="dur in videoDurations" :key="dur.duration" :value="dur.duration">{{ dur.name }}</option>
              </select>
            </div>
          </div>
          <div class="form-item">
            <label>视频尺寸</label>
            <select v-model="form.size" class="select">
              <option v-for="size in videoSizes" :key="size.name" :value="size.aspect_ratio">{{ size.name }}</option>
            </select>
          </div>
        </div>
        
        <!-- 数字人对口型 -->
        <div v-if="activeTab === 'avatar'" class="tab-content">
          <div class="form-row">
            <div class="form-item half">
              <label>上传数字人图片</label>
              <div class="upload-area" @click="triggerAvatarUpload">
                <input 
                  type="file" 
                  ref="avatarInput" 
                  style="display: none" 
                  accept="image/*" 
                  @change="handleAvatarUpload"
                >
                <div v-if="form.avatarImage" class="uploaded-image">
                  <img :src="form.avatarImage" alt="数字人图片" class="preview-image">
                  <button class="remove-btn" @click.stop="removeAvatar">×</button>
                </div>
                <div v-else class="upload-placeholder">
                  <div class="upload-icon">📁</div>
                  <p>请上传人物角色参考图片</p>
                  <p class="hint">支持 JPG、PNG 格式</p>
                </div>
              </div>
            </div>
            <div class="form-item half">
              <label>上传音频文件</label>
              <div class="upload-area" @click="triggerAudioUpload">
                <input 
                  type="file" 
                  ref="audioInput" 
                  style="display: none" 
                  accept="audio/*" 
                  @change="handleAudioUpload"
                >
                <div v-if="form.audioFile" class="uploaded-file">
                  <div class="file-icon">🎵</div>
                  <div class="file-info">
                    <p class="file-name">{{ form.audioFileName }}</p>
                  </div>
                  <button class="remove-btn" @click.stop="removeAudio">×</button>
                </div>
                <div v-else class="upload-placeholder">
                  <div class="upload-icon">📁</div>
                  <p>点击上传音频文件</p>
                  <p class="hint">支持 MP3、WAV 格式</p>
                </div>
              </div>
            </div>
          </div>
          <div class="form-item">
            <label>细节描述（可选）</label>
            <textarea
              v-model="form.detailPrompt"
              :rows="3"
              placeholder="请输入更多细节描述，如场景、角色、动作等"
              class="textarea"
            ></textarea>
          </div>
          <div class="form-row">
            <div class="form-item half">
              <label>模型选择</label>
              <div class="model-select-container">
                <div class="selected-model" @click="toggleModelDropdown('video')">
                  <img v-if="form.model && models[form.model]?.image_url" :src="models[form.model]?.image_url" :alt="models[form.model]?.name" class="model-icon">
                  <span v-if="form.model && models[form.model]">{{ models[form.model]?.name }}</span>
                  <span v-else>选择模型</span>
                  <span class="dropdown-arrow">▼</span>
                </div>
                <div v-if="modelDropdown === 'video'" class="model-dropdown">
                  <div class="model-group">
                    <div class="model-group-title">🌟 免费模型</div>
                    <div v-for="id in freeModels" :key="id" class="model-item" @click="selectModel('video', id)">
                      <img :src="models[id]?.image_url" :alt="models[id]?.name" class="model-icon">
                      <span class="model-name">{{ models[id]?.name }}</span>
                      <div class="model-tags">
                        <span v-if="models[id]?.is_new" class="model-tag new">NEW</span>
                        <span v-if="models[id]?.is_hot" class="model-tag hot">HOT</span>
                        <span v-if="models[id]?.discount > 0" class="model-tag discount">{{ models[id]?.discount }}% OFF</span>
                      </div>
                    </div>
                  </div>
                  <div class="model-group">
                    <div class="model-group-title">💎 付费模型</div>
                    <div v-for="id in paidModels" :key="id" class="model-item" @click="selectModel('video', id)">
                      <img :src="models[id]?.image_url" :alt="models[id]?.name" class="model-icon">
                      <span class="model-name">{{ models[id]?.name }}</span>
                      <div class="model-tags">
                        <span v-if="models[id]?.is_new" class="model-tag new">NEW</span>
                        <span v-if="models[id]?.is_hot" class="model-tag hot">HOT</span>
                        <span v-if="models[id]?.discount > 0" class="model-tag discount">{{ models[id]?.discount }}% OFF</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="model-hint" v-if="form.model && models[form.model]">
                <span :class="['model-type', models[form.model].type]">
                  {{ models[form.model].type === 'free' ? '免费' : '付费' }}
                </span>
                <span class="model-desc">{{ models[form.model].description }}</span>
              </div>
            </div>
            <div class="form-item half">
              <label>视频尺寸</label>
              <select v-model="form.size" class="select">
                <option v-for="size in videoSizes" :key="size.name" :value="size.aspect_ratio">{{ size.name }}</option>
              </select>
            </div>
          </div>
        </div>
        
        <!-- 生成按钮和进度条 -->
        <div class="generate-area">
          <button class="btn primary" @click="generateVideo" :disabled="loading || !isModelValid" :title="isModelValid ? '' : '当前模型不支持此功能，请选择其他模型'">
          {{ loading ? '生成中...' : '生成视频' }}
        </button>
          <div v-if="loading" class="progress-container">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progress + '%' }">
                <div class="progress-wave"></div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 生成结果 -->
        <div v-if="result" class="result-section">
          <h4>生成结果</h4>
          <div class="video-container">
            <video
              :src="result.video_url"
              :poster="result.thumbnail"
              controls
              class="result-video"
            ></video>
          </div>
          <div class="result-actions">
            <button class="btn secondary" @click="refreshVideo" :disabled="refreshCooldown > 0">
              {{ refreshCooldown > 0 ? `${refreshCooldown}s后刷新` : '🔄 刷新' }}
            </button>
          </div>
        </div>

        <AdBanner position="video-generate" />
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import { authMixin } from '../utils/authMixin'
import appCache from '../utils/appCache'
import AdBanner from '../components/AdBanner.vue'

export default {
  name: 'VideoGenerator',
  components: {
    AdBanner
  },
  mixins: [authMixin],
  data() {
    return {
      activeTab: 'text',
      form: {
        prompt: '',
        detailPrompt: '',
        model: '',
        duration: '',
        size: '16:9',
        referenceImage: '',
        avatarImage: '',
        audioFile: null,
        audioFileName: ''
      },
      loading: false,
      progress: 0,
      progressInterval: null,
      result: null,
      history: [],
      models: {},
      freeModels: [],
      paidModels: [],
      textToVideoModels: [],
      imageToVideoModels: [],
      avatarVideoModels: [],
      allVideoModels: [],
      modelDropdown: null,
      refreshCooldown: 0,
      refreshInterval: null,
      user_id: 0,
      videoDurations: [],
      videoSizes: []
    }
  },
  mounted() {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
    this.user_id = userInfo.id || 0
    // 从sessionStorage读取数据（解决URL参数长度限制问题）
    const videoParams = sessionStorage.getItem('videoParams')
    if (videoParams) {
      const params = JSON.parse(videoParams)
      if (params.referenceImage) {
        this.form.referenceImage = params.referenceImage
        this.activeTab = 'image'
      }
      if (params.prompt) {
        this.form.prompt = params.prompt
      }
      if (params.model) {
        this.form.model = params.model
      }
      if (params.duration) {
        this.form.duration = params.duration
      }
      if (params.size) {
        this.form.size = params.size
      }
      if (params.type) {
        this.activeTab = params.type === 'image' ? 'image' : (params.type === 'avatar' ? 'avatar' : 'text')
      }
      // 读取后清除，避免刷新后重复使用
      sessionStorage.removeItem('videoParams')
    }
    this.init()
  },
  computed: {
    // 检查当前选择的模型是否支持当前tab的功能
    isModelValid() {
      if (!this.form.model) return false
      const model = this.models[this.form.model]
      if (!model || !model.capabilities) return true // 没有capabilities字段，兼容旧数据
      
      if (this.activeTab === 'text') {
        return model.capabilities.includes('text_to_video')
      } else if (this.activeTab === 'image') {
        return model.capabilities.includes('image_to_video')
      } else if (this.activeTab === 'avatar') {
        return model.capabilities.includes('avatar_video')
      }
      return true
    }
  },
  watch: {
    // 当tab切换时，重新设置默认模型
    activeTab() {
      this.modelDropdown = null
      this.setDefaultModelForTab()
    }
  },
  methods: {
    particleStyle() {
      const size = Math.random() * 4 + 1
      const left = Math.random() * 100
      const top = Math.random() * 100
      const duration = Math.random() * 20 + 10
      const delay = Math.random() * 10
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        top: `${top}%`,
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`
      }
    },
    async init() {
      console.log('🚀 VideoGenerator 初始化开始')
      
      await Promise.all([
        this.loadHistory(),
        this.loadModels(),
        this.loadVideoDurations(),
        this.loadVideoSizes()
      ])
      
      console.log('🚀 VideoGenerator 初始化完成，videoSizes:', this.videoSizes)
    },
    
    async loadVideoDurations() {
      try {
        const response = await axios.get('/api/video-durations')
        if (response.data.code === 200) {
          this.videoDurations = response.data.video_durations
          if (this.videoDurations.length > 0) {
            this.form.duration = this.videoDurations[0].duration.toString()
          }
        }
      } catch (error) {
        console.error('加载视频时长失败', error)
      }
    },
    async loadVideoSizes() {
      try {
        const sizes = await appCache.getVideoSizes()
        if (sizes) {
          this.videoSizes = sizes
          if (this.videoSizes.length > 0) {
            this.form.size = this.videoSizes[0].aspect_ratio
          }
        }
      } catch (error) {
        console.error('加载视频尺寸失败', error)
      }
    },
    startProgress() {
      this.progress = 0
      this.progressInterval = setInterval(() => {
        if (this.progress < 90) {
          this.progress += Math.random() * 5 + 2.5
          if (this.progress > 90) this.progress = 90
        }
      }, 500)
    },
    stopProgress() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
      this.progress = 100
      setTimeout(() => {
        this.progress = 0
      }, 300)
    },
    startRefreshCooldown() {
      this.refreshCooldown = 10
      this.refreshInterval = setInterval(() => {
        if (this.refreshCooldown > 0) {
          this.refreshCooldown--
        } else {
          clearInterval(this.refreshInterval)
          this.refreshInterval = null
        }
      }, 1000)
    },
    triggerImageUpload() {
      this.$refs.imageInput.click()
    },
    handleImageUpload(event) {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.form.referenceImage = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    removeImage() {
      this.form.referenceImage = ''
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = ''
      }
    },
    triggerAvatarUpload() {
      this.$refs.avatarInput.click()
    },
    handleAvatarUpload(event) {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.form.avatarImage = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    removeAvatar() {
      this.form.avatarImage = ''
      if (this.$refs.avatarInput) {
        this.$refs.avatarInput.value = ''
      }
    },
    triggerAudioUpload() {
      this.$refs.audioInput.click()
    },
    handleAudioUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.form.audioFile = file
        this.form.audioFileName = file.name
      }
    },
    removeAudio() {
      this.form.audioFile = null
      this.form.audioFileName = ''
      if (this.$refs.audioInput) {
        this.$refs.audioInput.value = ''
      }
    },
    async generateVideo() {
      // 检查模型能力
      if (!this.isModelValid) {
        let capabilityName = ''
        if (this.activeTab === 'text') capabilityName = '文生视频'
        else if (this.activeTab === 'image') capabilityName = '图生视频'
        else if (this.activeTab === 'avatar') capabilityName = '数字人'
        alert(`当前模型不支持${capabilityName}功能，请选择其他模型`)
        return
      }
      
      // 验证输入
      if (this.activeTab === 'text' && !this.form.prompt) {
        console.log('请输入提示词')
        return
      }
      if (this.activeTab === 'image' && !this.form.referenceImage) {
        console.log('请上传参考图片')
        return
      }
      if (this.activeTab === 'avatar' && (!this.form.avatarImage || !this.form.audioFile)) {
        console.log('请上传数字人图片和音频文件')
        return
      }
      
      // 构建查询参数
      const query = {
        prompt: this.activeTab === 'avatar' ? this.form.detailPrompt : this.form.prompt,
        model: this.form.model,
        duration: this.form.duration,
        size: this.form.size,
        type: this.activeTab
      }
      
      // 如果有图片，用sessionStorage存储，因为base64太长不适合放URL
      if (this.form.referenceImage) {
        query.hasReferenceImage = '1'
        sessionStorage.setItem('videoReferenceImage', this.form.referenceImage)
      }
      if (this.form.avatarImage) {
        query.hasAvatarImage = '1'
        sessionStorage.setItem('videoAvatarImage', this.form.avatarImage)
      }
      if (this.form.audioFile && this.form.audioFileName) {
        const reader = new FileReader()
        const audioBase64 = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result)
          reader.readAsDataURL(this.form.audioFile)
        })
        sessionStorage.setItem('videoAudioFile', audioBase64)
        sessionStorage.setItem('videoAudioFileName', this.form.audioFileName)
        query.hasAudioFile = '1'
        query.audioFileName = this.form.audioFileName
      }
      
      // 跳转到 GeneratorPro 页面
      this.$router.push({
        path: '/video-generator-pro',
        query: query
      })
    },
    async refreshVideo() {
      if (this.refreshCooldown > 0) return
      this.startRefreshCooldown()
      await this.generateVideo()
    },
    async loadHistory() {
      try {
        const response = await axios.get('/api/video-history', {
          params: { user_id: this.user_id }
        })
        this.history = response.data
      } catch (error) {
        console.error('加载历史记录失败', error)
      }
    },
    async loadModels() {
      try {
        // 加载所有视频模型
        const allVideoModels = await appCache.getModelsByCategory('video')
        // 加载支持文生视频的模型
        const textToVideoModels = await appCache.getModelsByCategory('video', false, 'text_to_video')
        // 加载支持图生视频的模型
        const imageToVideoModels = await appCache.getModelsByCategory('video', false, 'image_to_video')
        // 加载支持数字人的模型
        const avatarVideoModels = await appCache.getModelsByCategory('video', false, 'avatar_video')
        
        this.models = {}
        this.freeModels = []
        this.paidModels = []
        this.textToVideoModels = []
        this.imageToVideoModels = []
        this.avatarVideoModels = []
        this.allVideoModels = []
        
        // 处理所有视频模型（用于初始化 free/paid）
        allVideoModels.forEach(model => {
          this.models[model.identifier] = {
            name: model.name,
            description: model.description,
            type: model.model_type,
            image_url: model.image_url,
            is_new: model.is_new,
            is_hot: model.is_hot,
            discount: model.discount,
            capabilities: model.capabilities || []
          }
          if (model.model_type === 'free') {
            this.freeModels.push(model.identifier)
          } else {
            this.paidModels.push(model.identifier)
          }
        })
        
        // 按能力分类
        textToVideoModels.forEach(model => {
          this.textToVideoModels.push(model.identifier)
        })
        imageToVideoModels.forEach(model => {
          this.imageToVideoModels.push(model.identifier)
        })
        avatarVideoModels.forEach(model => {
          this.avatarVideoModels.push(model.identifier)
        })
        
        // 根据当前tab设置默认模型
        this.setDefaultModelForTab()
        
        // 从后台配置获取默认模型、分辨率和时长
        try {
          const response = await axios.get('/admin/config/video')
          if (response.data.code === 200) {
            const config = response.data.data
            const defaultVideoSize = config.defaultVideoSize || '1920x1080 (16:9)'
            const defaultDuration = config.defaultDuration || '15'
            
            // 设置默认分辨率
            if (defaultVideoSize) {
              const matchedSize = this.videoSizes.find(s => s.name === defaultVideoSize)
              if (matchedSize) {
                this.form.size = matchedSize.aspect_ratio
              } else {
                // 如果没匹配到，尝试从配置值中提取宽高比
                const ratioMatch = defaultVideoSize.match(/\((\d+:\d+)\)/)
                if (ratioMatch) {
                  const matchedByRatio = this.videoSizes.find(s => s.name.includes(ratioMatch[1]))
                  if (matchedByRatio) {
                    this.form.size = matchedByRatio.aspect_ratio
                  }
                }
              }
            }
            
            // 设置默认时长
            if (defaultDuration) {
              this.form.duration = defaultDuration
            }
          }
        } catch (error) {
          console.error('获取视频配置失败:', error)
        }
      } catch (error) {
        console.error('加载模型列表失败', error)
      }
    },
    
    setDefaultModelForTab() {
      // 根据当前tab选择对应能力的第一个免费模型作为默认
      let targetModels = []
      if (this.activeTab === 'text') {
        targetModels = this.textToVideoModels
      } else if (this.activeTab === 'image') {
        targetModels = this.imageToVideoModels
      } else if (this.activeTab === 'avatar') {
        targetModels = this.avatarVideoModels
      }
      
      // 选择第一个免费模型，没有则选第一个
      const defaultModel = targetModels.find(id => this.freeModels.includes(id)) || targetModels[0]
      if (defaultModel) {
        this.form.model = defaultModel
      }
    },
    toggleModelDropdown(type) {
      if (this.modelDropdown === type) {
        this.modelDropdown = null
      } else {
        this.modelDropdown = type
      }
    },
    selectModel(type, modelId) {
      this.form.model = modelId
      this.modelDropdown = null
    }
  }
}
</script>

<style scoped>
.section-drama {
  padding-top: 100px;
  padding-bottom: 60px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #0a0e1a 0%, #0d1224 50%, #0a0e1a 100%);
  position: relative;
  min-height: 100vh;
}

.tech-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.grid-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 230, 172, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 230, 172, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

.floating-particles {
  position: absolute;
  width: 100%;
  height: 100%;
}

.particle {
  position: absolute;
  background: rgba(0, 230, 172, 0.4);
  border-radius: 50%;
  animation: floatParticle linear infinite;
  filter: blur(1px);
  box-shadow: 0 0 8px rgba(0, 230, 172, 0.3);
}

@keyframes floatParticle {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-100vh) translateX(50px); opacity: 0; }
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.15;
}

.glow-orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(0, 204, 153, 0.6) 0%, transparent 70%);
  top: 20%;
  left: 10%;
  animation: floatOrb1 15s ease-in-out infinite;
}

.glow-orb-2 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 230, 172, 0.6) 0%, transparent 70%);
  top: 50%;
  right: 15%;
  animation: floatOrb2 18s ease-in-out infinite;
}

.glow-orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(79, 172, 254, 0.6) 0%, transparent 70%);
  bottom: 10%;
  left: 50%;
  animation: floatOrb3 20s ease-in-out infinite;
}

@keyframes floatOrb1 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(100px, -50px); }
}

@keyframes floatOrb2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-80px, 80px); }
}

@keyframes floatOrb3 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-60px, -100px); }
}

.hex-grid {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%2300e6ac' fill-opacity='0.02'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.4;
}

.container {
  position: relative;
  z-index: 1;
}

.page-head {
  text-align: center;
  margin-bottom: 40px;
}

.page-head h2 {
  font-size: 32px;
  margin-bottom: 10px;
}

.page-head p {
  color: #888;
  font-size: 16px;
}

.card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  padding: 40px;
  max-width: 900px;
  margin: 0 auto;
}

/* 标签页样式 */
.tab-container {
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding-bottom: 10px;
}

.tab-btn {
  padding: 12px 24px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #aaa;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.tab-btn:hover {
  background: rgba(255,255,255,0.05);
  color: #fff;
}

.tab-btn.active {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  border-color: #00e6ac;
  color: #000;
  font-weight: 600;
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 上传区域样式 */
.upload-area {
  border: 2px dashed rgba(255,255,255,0.1);
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.upload-area:hover {
  border-color: #00e6ac;
  background: rgba(0, 230, 172, 0.05);
}

.upload-placeholder {
  color: #666;
}

.upload-icon {
  font-size: 32px;
  margin-bottom: 10px;
}

.upload-placeholder p {
  margin: 5px 0;
  font-size: 14px;
}

.upload-placeholder .hint {
  font-size: 12px;
  color: #888;
}

.uploaded-image {
  position: relative;
  display: inline-block;
}

.preview-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
}

.uploaded-file {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px;
  background: rgba(255,255,255,0.05);
  border-radius: 8px;
}

.file-icon {
  font-size: 24px;
}

.file-info {
  text-align: left;
  flex: 1;
}

.file-name {
  margin: 0;
  font-size: 14px;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.remove-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 24px;
  height: 24px;
  background: #ff4d4f;
  border: none;
  border-radius: 50%;
  color: #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 表单样式 */
.form-item {
  margin-bottom: 20px;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.form-item.half {
  flex: 1;
}

label {
  display: block;
  margin-bottom: 8px;
  color: #aaa;
  font-size: 14px;
}

.textarea {
  width: 100%;
  padding: 12px 16px;
  background: #1a1e2d;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  outline: none;
  resize: vertical;
  font-family: inherit;
}

.textarea:focus {
  border-color: #00e6ac;
}

.select {
  width: 100%;
  padding: 12px 16px;
  background: #1a1e2d;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  outline: none;
}

.select:focus {
  border-color: #00e6ac;
}

/* 生成区域样式 */
.generate-area {
  display: flex;
  gap: 20px;
  align-items: center;
  margin-top: 30px;
}

.progress-container {
  flex: 1;
}

.progress-bar {
  width: 100%;
  height: 14px;
  background: rgba(255,255,255,0.1);
  border-radius: 7px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00e6ac, #00cc99, #00e6ac);
  border-radius: 7px;
  position: relative;
  transition: width 0.3s ease;
  background-size: 200% 100%;
  animation: gradientMove 2s linear infinite;
}

@keyframes gradientMove {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}

.progress-wave {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    -45deg,
    transparent 33%,
    rgba(255,255,255,0.3) 33%,
    rgba(255,255,255,0.3) 66%,
    transparent 66%
  );
  background-size: 20px 20px;
  animation: waveMove 1s linear infinite;
}

@keyframes waveMove {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 20px 0;
  }
}

/* 结果区域样式 */
.result-section {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid rgba(255,255,255,0.05);
}

.result-section h4 {
  font-size: 18px;
  margin-bottom: 20px;
}

.video-container {
  width: 100%;
  max-width: 800px;
  margin: 0 auto 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.result-video {
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: contain;
  border-radius: 8px;
  background: #000;
}

.result-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
}

/* 按钮样式 */
.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn.primary {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  color: #000;
}

.btn.primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 230, 172, 0.3);
}

.btn.secondary {
  background: rgba(255,255,255,0.1);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
}

.btn.secondary:hover:not(:disabled) {
  background: rgba(255,255,255,0.15);
  border-color: #00e6ac;
}

/* 模型提示样式 */
.model-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #888;
}

.model-type {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  margin-right: 8px;
}

.model-type.free {
  background: rgba(0, 230, 172, 0.1);
  color: #00e6ac;
}

.model-type.paid {
  background: rgba(255, 152, 0, 0.1);
  color: #ff9800;
}

.model-desc {
  color: #666;
}

/* 模型卡片样式 */
.model-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1a1e2d;
}

.model-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.model-card:hover {
  border-color: #00e6ac;
  background: rgba(0, 230, 172, 0.05);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 230, 172, 0.2);
}

.model-card.active {
  border-color: #00e6ac;
  background: rgba(0, 230, 172, 0.1);
  box-shadow: 0 0 0 2px rgba(0, 230, 172, 0.3);
}

.model-image-container {
  position: relative;
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
  background: #0d0f16;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.model-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.model-card:hover .model-image {
  transform: scale(1.05);
}

.model-badges {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  gap: 6px;
  z-index: 2;
}

.badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.badge.new {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  color: #000;
}

.badge.hot {
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: #fff;
}

.badge.discount {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: #fff;
}

.model-info {
  position: relative;
  z-index: 1;
}

.model-name {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.model-description {
  font-size: 12px;
  color: #aaa;
  margin: 0 0 12px 0;
  line-height: 1.4;
  height: 40px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.model-type-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.model-type-badge.free {
  background: rgba(0, 230, 172, 0.1);
  color: #00e6ac;
  border: 1px solid rgba(0, 230, 172, 0.3);
}

.model-type-badge.paid {
  background: rgba(255, 152, 0, 0.1);
  color: #ff9800;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

/* 模型标签样式 */
.model-tag {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 4px;
  vertical-align: middle;
}

.model-tag.new {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  color: #000;
}

.model-tag.hot {
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: #fff;
}

.model-tag.discount {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: #fff;
}

/* 模型选择下拉菜单样式 */
.model-select-container {
  position: relative;
  width: 100%;
}

.selected-model {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.selected-model:hover {
  border-color: #00e6ac;
  background: rgba(0, 230, 172, 0.05);
}

.model-icon {
  width: 24px;
  height: 24px;
  margin-right: 10px;
  border-radius: 4px;
}

.dropdown-arrow {
  margin-left: auto;
  font-size: 12px;
  color: #999;
  transition: transform 0.3s ease;
}

.model-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #1a1e2d;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-top: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

.model-group {
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.model-group:last-child {
  border-bottom: none;
}

.model-group-title {
  padding: 0 15px 10px;
  font-size: 14px;
  font-weight: 600;
  color: #00e6ac;
}

.model-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.model-item:hover {
  background: rgba(0, 230, 172, 0.1);
}

.model-item .model-name {
  flex: 1;
  margin-right: 10px;
}

.model-tags {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.model-desc {
  color: #666;
}

/* 响应式样式 */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }
  .card {
    padding: 20px;
  }
  .generate-area {
    flex-direction: column;
    align-items: stretch;
  }
  .progress-container {
    width: 100%;
  }
  .tab-container {
    flex-direction: column;
  }
  .tab-btn {
    width: 100%;
  }
}
</style>
